import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { evenGrossParts, fromCents, reconcileParts, splitVat, toCents } from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { EtDateInput } from '@/components/ui/EtDateInput';
import {
  formatEur,
  PRIORITY_LABEL,
  type CashFlowEntry,
  type CreateCashFlowEntryInput,
  type CreateCashFlowScheduleInput,
} from '@/lib/cashFlow';
import { CERTAINTY_GLYPH, CERTAINTY_LABEL } from './calendar/model';

/**
 * Module 1.3 — the ONE shared "Lisa rahavoo kirje" creation dialog (replit.md L14).
 * Every lens — calendar day "+", table row "+", register "+", and the edge floating
 * "+" — opens THIS exact component, so a manual entry is created identically from
 * anywhere (a per-lens create form would be an L14 defect). Covers every editable
 * `pgfin.cash_flow_entry` field. `source` is locked to `manual` (the only thing a
 * human creates here; synced rows come from SmartAccounts/Erply/LHV). Money is
 * entered in euros and converted to integer cents at the boundary (L9, via
 * finance-core `toCents`); the live NET/VAT/GROSS preview is finance-core `splitVat`
 * — the SAME function the API uses — never inline VAT arithmetic in the component.
 */

type Kind = CreateCashFlowEntryInput['kind'];
type Basis = CreateCashFlowEntryInput['amount']['basis'];
type Category = NonNullable<CashFlowEntry['category']>;
type DocType = NonNullable<CashFlowEntry['docType']>;
type Priority = NonNullable<CashFlowEntry['priority']>;
/** The three VAT modes the approved canvas offers (domestic 24 / foreign 0 / reduced 13·9). */
type DialogVatMode = 'domestic' | 'foreign' | 'reduced';

/** 7-line taxonomy + its Estonian label + the direction it belongs to. */
const CATEGORY: Record<Category, { label: string; kind: Kind }> = {
  sale: { label: 'Müük', kind: 'income' },
  direct_cost: { label: 'Otsekulud', kind: 'cost' },
  operating_cost: { label: 'Tegevuskulud', kind: 'cost' },
  payroll: { label: 'Tööjõukulud', kind: 'cost' },
  financial_cost: { label: 'Finantskulud', kind: 'cost' },
  tax_agreement: { label: 'Maksud', kind: 'cost' },
  loan: { label: 'Laenud', kind: 'cost' },
};

/** The default category each direction lands on (no fragile array-find/`!`). */
const FIRST_CATEGORY: Record<Kind, Category> = { income: 'sale', cost: 'operating_cost' };

const DOC_TYPE: Record<DocType, string> = {
  invoice: 'Arve',
  prepayment: 'Ettemaks',
  credit_note: 'Kreeditarve',
  offer: 'Pakkumine',
  delivery_invoice: 'Saateleht-arve',
  none: 'Dokumendita / muu',
};

const VAT_MODE: Record<DialogVatMode, string> = {
  domestic: 'Riigisisene',
  foreign: 'Välismaa (0%)',
  reduced: 'Vähendatud',
};

/** Allowed rates per mode (replit.md L11: 24 domestic / 0 foreign / 13·9 reduced). */
const RATES_FOR: Record<DialogVatMode, number[]> = {
  domestic: [24],
  foreign: [0],
  reduced: [13, 9],
};

const inputCls =
  'h-9 rounded-md border border-border bg-background/60 px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-[hsl(var(--primary))]/70 focus:ring-1 focus:ring-[hsl(var(--primary))]/40';

/**
 * A labelled field. It is a plain <div> (NOT a <label>): the field holds segmented
 * button-groups as well as inputs, and wrapping interactive controls in a <label>
 * corrupts their accessible names. Every control carries its own aria-label instead.
 */
function Field({ label, hint, children, full }: { label: string; hint?: string; children: ReactNode; full?: boolean }) {
  return (
    <div className={cn('flex flex-col gap-1.5', full && 'col-span-2')}>
      <span className="flex items-baseline justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        {hint && <span className="text-[10px] text-muted-foreground/60">{hint}</span>}
      </span>
      {children}
    </div>
  );
}

interface SegOption<T extends string> {
  v: T;
  label: string;
  glyph?: string;
  activeClass: string;
}

/** A compact segmented toggle (Suund / Kindlus / Neto–Bruto) — the dark-dense kit. */
function Seg<T extends string>({
  value,
  onValue,
  options,
  ariaLabel,
}: {
  value: T;
  onValue: (v: T) => void;
  options: SegOption<T>[];
  ariaLabel: string;
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex w-full items-center rounded-md border border-border p-0.5">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          aria-pressed={value === o.v}
          onClick={() => onValue(o.v)}
          className={cn(
            'flex-1 rounded px-2.5 py-1.5 text-xs font-medium transition-colors',
            value === o.v ? o.activeClass : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.glyph && <span className="mr-1">{o.glyph}</span>}
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface CreateEntryDialogProps {
  /** Whether the dialog is shown; when false the component renders nothing. */
  open: boolean;
  /** Pre-fill the expected date (ISO) — the clicked calendar day, the row's day, or today for the FAB. */
  initialDate?: string;
  /** Pre-select the direction (a lens may open the dialog already knowing income/cost). */
  initialKind?: Kind;
  /** Pre-select the category (consistent with `initialKind`). */
  initialCategory?: Category;
  /** Emits the validated create body (cents already computed). The parent fires the mutation + closes. */
  onSubmit: (input: CreateCashFlowEntryInput) => void;
  /**
   * Emits the validated SCHEDULE body ("Osadena" — N instalments, cents per part
   * already computed). The parent fires the schedule mutation + closes. Required: the
   * ONE shared dialog is opened identically from every lens, so scheduling is always
   * available (L14 lens parity — a capability never exists in one lens only).
   */
  onSubmitSchedule: (input: CreateCashFlowScheduleInput) => void;
  /** Dismiss without creating (✕ / backdrop / Tühista). */
  onClose: () => void;
}

/** One editable instalment row in the "Osadena" schedule (ISO due day + euro amount). */
interface PartDraft {
  date: string;
  amount: string;
}

export function CreateEntryDialog({
  open,
  initialDate,
  initialKind,
  initialCategory,
  onSubmit,
  onSubmitSchedule,
  onClose,
}: CreateEntryDialogProps) {
  const [kind, setKind] = useState<Kind>(initialKind ?? 'cost');
  const [certainty, setCertainty] = useState<CashFlowEntry['certainty']>('forecast');
  const [category, setCategory] = useState<Category>(initialCategory ?? FIRST_CATEGORY[initialKind ?? 'cost']);
  const [party, setParty] = useState('');
  const [description, setDescription] = useState('');
  const [docType, setDocType] = useState<DocType>('invoice');
  const [expectedDate, setExpectedDate] = useState(initialDate ?? '');
  const [basis, setBasis] = useState<Basis>('gross');
  const [amount, setAmount] = useState('');
  const [vatMode, setVatMode] = useState<DialogVatMode>('domestic');
  const [vatRate, setVatRate] = useState(24);
  const [showMore, setShowMore] = useState(false);
  const [project, setProject] = useState('');
  const [priority, setPriority] = useState<Priority | ''>('');
  const [comment, setComment] = useState('');
  // Payment schedule ("Osadena"): whether the amount is paid in >=2 instalments, and
  // the editable instalment rows (a due day + a euro amount per part).
  const [scheduled, setScheduled] = useState(false);
  const [parts, setParts] = useState<PartDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Re-initialise from the props each time the dialog opens (a new day/direction).
  useEffect(() => {
    if (!open) return;
    const k = initialKind ?? 'cost';
    setKind(k);
    setCertainty('forecast');
    setCategory(initialCategory ?? FIRST_CATEGORY[k]);
    setParty('');
    setDescription('');
    setDocType('invoice');
    setExpectedDate(initialDate ?? '');
    setBasis('gross');
    setAmount('');
    setVatMode('domestic');
    setVatRate(24);
    setShowMore(false);
    setProject('');
    setPriority('');
    setComment('');
    setScheduled(false);
    setParts([
      { date: initialDate ?? '', amount: '' },
      { date: '', amount: '' },
    ]);
    setError(null);
  }, [open, initialKind, initialCategory, initialDate]);

  // Money never touches JS-float maths (L9): toCents absorbs binary drift; a blank/
  // non-positive amount reads as 0 cents (the preview shows zero, submit is blocked).
  const eurToCents = (s: string): number => {
    const e = Number(s.replace(',', '.'));
    return Number.isFinite(e) && e > 0 ? toCents(e) : 0;
  };
  const cents = eurToCents(amount);
  // The live preview is the SAME finance-core split the API runs (parity, not a copy).
  const split = useMemo(
    () => splitVat({ amountCents: cents, basis, vatRate, vatMode }),
    [cents, basis, vatRate, vatMode],
  );

  // Schedule reconciliation — sum · remaining · balanced computed in finance-core
  // (never money maths in the component, L9). Parts are GROSS cents; they must sum to
  // the entry's total gross for the schedule to be submittable.
  const partCents = parts.map((p) => eurToCents(p.amount));
  const recon = reconcileParts(split.gross, partCents);

  const setPartCount = (n: number) => {
    setParts((prev) => {
      if (n <= prev.length) return prev.slice(0, n);
      return [...prev, ...Array.from({ length: n - prev.length }, () => ({ date: '', amount: '' }))];
    });
  };
  const updatePart = (i: number, patch: Partial<PartDraft>) => {
    setParts((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  };
  /** "Jaga võrdselt": spread the entered total gross evenly across the parts (finance-core). */
  const splitEvenly = () => {
    const even = evenGrossParts(split.gross, parts.length);
    setParts((prev) => prev.map((p, i) => ({ ...p, amount: String(fromCents(even[i])) })));
  };

  const categoryOptions = (Object.keys(CATEGORY) as Category[]).filter((c) => CATEGORY[c].kind === kind);
  const isForecast = certainty === 'forecast';
  const accentVar = kind === 'income' ? '--income' : '--cost';

  function onKind(next: Kind) {
    setKind(next);
    setCategory(FIRST_CATEGORY[next]);
  }
  function onVatMode(next: DialogVatMode) {
    setVatMode(next);
    setVatRate(RATES_FOR[next][0]);
  }

  function submit() {
    if (!party.trim() || cents <= 0) {
      setError('Sisesta vastaspool ja summa (> 0).');
      return;
    }
    // The fields both a single create and a schedule share (everything but the date).
    const common = {
      kind,
      party: party.trim(),
      description: description.trim() || undefined,
      category,
      certainty,
      source: 'manual' as const,
      amount: { cents, basis },
      vatRate,
      vatMode,
      docType,
      project: project.trim() || undefined,
      comment: comment.trim() || undefined,
      priority: priority || undefined,
    };

    if (scheduled) {
      if (parts.some((p) => p.date === '')) {
        setError('Määra iga osamakse kuupäev.');
        return;
      }
      if (!recon.balanced) {
        setError('Osamaksete summa peab võrduma bruto kogusummaga.');
        return;
      }
      onSubmitSchedule({
        ...common,
        schedule: parts.map((p, i) => ({ expectedDate: p.date, cents: partCents[i] })),
      });
      return;
    }

    onSubmit({ ...common, expectedDate });
  }

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" aria-label="Sulge taust" className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Lisa rahavoo kirje"
        className={cn(
          'relative flex w-[560px] max-w-full flex-col overflow-hidden rounded-xl border bg-card shadow-2xl',
          isForecast ? 'border-dashed border-[hsl(var(--pending))]/55' : 'border-border',
        )}
      >
        <div className="flex items-center gap-3 border-b border-border bg-gradient-to-b from-card to-card/70 px-5 py-3.5">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-md text-base font-bold"
            style={{ background: `hsl(var(${accentVar}) / .16)`, color: `hsl(var(${accentVar}))` }}
          >
            {kind === 'income' ? '+' : '−'}
          </span>
          <div className="flex flex-col">
            <h2 className="text-sm font-semibold text-foreground">Lisa rahavoo kirje</h2>
            <span className="text-[11px] text-muted-foreground">Käsitsi sisestus · arvutab KM automaatselt</span>
          </div>
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[hsl(var(--primary))]">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))]" /> source: manual
          </span>
          <button
            type="button"
            onClick={onClose}
            className="ml-1 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Sulge"
          >
            ✕
          </button>
        </div>

        <div className="flex max-h-[560px] flex-col gap-4 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Suund" full>
              <Seg
                ariaLabel="Suund"
                value={kind}
                onValue={onKind}
                options={[
                  { v: 'income', label: 'Tulu', activeClass: 'bg-[hsl(var(--income))]/20 text-[hsl(var(--income))]' },
                  { v: 'cost', label: 'Kulu', activeClass: 'bg-[hsl(var(--cost))]/20 text-[hsl(var(--cost))]' },
                ]}
              />
            </Field>
            <Field label="Kindlus" hint="prognoos → rõhutatud" full>
              <Seg
                ariaLabel="Kindlus"
                value={certainty}
                onValue={setCertainty}
                options={(['forecast', 'committed', 'actual'] as CashFlowEntry['certainty'][]).map((c) => ({
                  v: c,
                  label: CERTAINTY_LABEL[c],
                  glyph: CERTAINTY_GLYPH[c],
                  activeClass:
                    c === 'forecast'
                      ? 'bg-[hsl(var(--pending))]/20 text-[hsl(var(--pending))]'
                      : 'bg-accent text-foreground',
                }))}
              />
            </Field>
          </div>

          <Field label="Kategooria" hint="7-realine taksonoomia">
            <select
              className={inputCls}
              value={category}
              onChange={(e) => setCategory(e.target.value as Category)}
              aria-label="Kategooria"
            >
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY[c].label}
                </option>
              ))}
            </select>
          </Field>

          <div className="h-px bg-border" />

          <div className="grid grid-cols-2 gap-3">
            <Field label="Vastaspool" full>
              <input
                className={inputCls}
                value={party}
                onChange={(e) => setParty(e.target.value)}
                placeholder="nt Rentest AS"
                aria-label="Vastaspool"
              />
            </Field>
            <Field label="Dokument">
              <select
                className={inputCls}
                value={docType}
                onChange={(e) => setDocType(e.target.value as DocType)}
                aria-label="Dokument"
              >
                {(Object.keys(DOC_TYPE) as DocType[]).map((d) => (
                  <option key={d} value={d}>
                    {DOC_TYPE[d]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Kirjeldus">
              <input
                className={inputCls}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="lühikirjeldus"
                aria-label="Kirjeldus"
              />
            </Field>
          </div>

          {/* Oodatav kuupäev — a single due day, OR (Osadena) a whole payment schedule.
              Both live in the ONE dialog: the same fields, only the date side splits. */}
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-background/40 p-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {scheduled ? 'Maksegraafik' : 'Oodatav kuupäev'}
                <span className="ml-2 normal-case text-[10px] text-muted-foreground/60">Europe/Tallinn</span>
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={scheduled}
                aria-label="Osadena"
                onClick={() => setScheduled((s) => !s)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors',
                  scheduled
                    ? 'border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                <span className={cn('inline-block h-1.5 w-1.5 rounded-full', scheduled ? 'bg-[hsl(var(--primary))]' : 'bg-muted-foreground/40')} />
                Osadena
              </button>
            </div>

            {!scheduled && (
              <EtDateInput className={inputCls} value={expectedDate} onChangeIso={setExpectedDate} aria-label="Oodatav kuupäev" />
            )}

            {scheduled && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <label className="text-[11px] text-muted-foreground" htmlFor="osade-arv">
                    Osade arv
                  </label>
                  <input
                    id="osade-arv"
                    type="number"
                    min={2}
                    max={24}
                    value={parts.length}
                    onChange={(e) => setPartCount(Math.max(2, Math.min(24, Math.trunc(Number(e.target.value) || 2))))}
                    aria-label="Osade arv"
                    className={cn(inputCls, 'w-20 text-right tabular-nums')}
                  />
                  <button
                    type="button"
                    onClick={splitEvenly}
                    className="ml-auto rounded-md border border-border px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    Jaga võrdselt
                  </button>
                </div>

                <div className="flex flex-col gap-1.5">
                  {parts.map((p, i) => (
                    <div key={i} className="grid grid-cols-[auto_1fr_7rem] items-center gap-2">
                      <span className="w-10 text-[11px] tabular-nums text-muted-foreground">#{i + 1}</span>
                      <EtDateInput
                        className={inputCls}
                        value={p.date}
                        onChangeIso={(iso) => updatePart(i, { date: iso })}
                        aria-label={`Osa ${i + 1} kuupäev`}
                      />
                      <input
                        inputMode="decimal"
                        value={p.amount}
                        onChange={(e) => updatePart(i, { amount: e.target.value })}
                        aria-label={`Osa ${i + 1} summa (€)`}
                        placeholder="€"
                        className={cn(inputCls, 'text-right tabular-nums')}
                      />
                    </div>
                  ))}
                </div>

                {/* Reconciliation bar — sum · remaining · balanced from finance-core (L9). */}
                <div
                  className={cn(
                    'flex items-center justify-between rounded-md border px-3 py-1.5 text-[11px]',
                    recon.balanced
                      ? 'border-[hsl(var(--income))]/40 bg-[hsl(var(--income))]/10 text-[hsl(var(--income))]'
                      : 'border-[hsl(var(--pending))]/40 bg-[hsl(var(--pending))]/10 text-[hsl(var(--pending))]',
                  )}
                  role="status"
                >
                  <span className="tabular-nums">
                    Jagatud {formatEur(recon.sum)} / {formatEur(split.gross)}
                  </span>
                  <span className="tabular-nums font-medium">
                    {recon.balanced ? '✓ klapib' : `jääk ${formatEur(recon.remaining)}`}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="h-px bg-border" />

          <div className="grid grid-cols-2 gap-3">
            <Field label="Sisestan summana" hint="neto või bruto">
              <Seg
                ariaLabel="Summa alus"
                value={basis}
                onValue={setBasis}
                options={[
                  { v: 'net', label: 'Neto', activeClass: 'bg-accent text-foreground' },
                  { v: 'gross', label: 'Bruto', activeClass: 'bg-accent text-foreground' },
                ]}
              />
            </Field>
            <Field label={basis === 'gross' ? 'Bruto summa (€)' : 'Neto summa (€)'}>
              <input
                inputMode="decimal"
                className={cn(inputCls, 'text-right tabular-nums')}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                aria-label="Summa"
              />
            </Field>
            <Field label="KM režiim">
              <select
                className={inputCls}
                value={vatMode}
                onChange={(e) => onVatMode(e.target.value as DialogVatMode)}
                aria-label="KM režiim"
              >
                {(Object.keys(VAT_MODE) as DialogVatMode[]).map((m) => (
                  <option key={m} value={m}>
                    {VAT_MODE[m]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="KM määr">
              <select
                className={inputCls}
                value={vatRate}
                onChange={(e) => setVatRate(Number(e.target.value))}
                aria-label="KM määr"
              >
                {RATES_FOR[vatMode].map((r) => (
                  <option key={r} value={r}>
                    {r}%
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Live finance-core split (NET/VAT/GROSS) — data, not a money-view mode (v1.4). */}
          <div className="grid grid-cols-3 gap-2 rounded-lg border border-border bg-background/50 p-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Neto</span>
              <span className="tabular-nums text-sm font-semibold text-foreground">{formatEur(split.net)}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">KM {vatRate}%</span>
              <span className="tabular-nums text-sm font-semibold text-[hsl(var(--vatmode))]">{formatEur(split.vat)}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Bruto</span>
              <span className="tabular-nums text-sm font-semibold text-foreground">{formatEur(split.gross)}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowMore((m) => !m)}
            className="flex items-center gap-1.5 self-start text-[11px] font-medium text-muted-foreground hover:text-foreground"
          >
            <span className={cn('transition-transform', showMore && 'rotate-90')}>›</span>
            Lisaväljad (projekt · prioriteet · märkus)
          </button>
          {showMore && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Projekt">
                <input className={inputCls} value={project} onChange={(e) => setProject(e.target.value)} aria-label="Projekt" />
              </Field>
              <Field label="Prioriteet">
                <select
                  className={inputCls}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as Priority | '')}
                  aria-label="Prioriteet"
                >
                  <option value="">—</option>
                  {(Object.keys(PRIORITY_LABEL) as Priority[]).map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_LABEL[p]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Märkus" full>
                <textarea
                  className={cn(inputCls, 'h-16 resize-none py-2')}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="vaba tekst"
                  aria-label="Märkus"
                />
              </Field>
            </div>
          )}

          {error && (
            <p role="alert" className="text-xs text-[hsl(var(--cost))]">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border bg-card/80 px-5 py-3">
          <span className="text-[11px] text-muted-foreground">
            Loodud kirje on <span className="text-foreground/80">kustutatav</span> · hiljem ühildub sünkroonitud tegelikuga
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-3.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              Tühista
            </button>
            <button
              type="button"
              onClick={submit}
              className="rounded-md bg-[hsl(var(--primary))] px-4 py-1.5 text-xs font-semibold text-[hsl(var(--primary-foreground))] shadow hover:brightness-110"
            >
              {scheduled ? `Lisa graafik · ${parts.length} osa` : `Lisa kirje · ${formatEur(split.gross)}`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
